import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Star, Loader2, Check, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ModuleFeedback({ moduleId, chapter, onComplete }) {
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [difficulty, setDifficulty] = useState(0);
    const [usefulness, setUsefulness] = useState(0);
    const [comment, setComment] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async () => {
        if (rating === 0) { toast.error('Selecciona una calificación'); return; }
        setLoading(true);
        try {
            await axios.post(`${API_URL}/api/user/feedback`, {
                module_id: moduleId,
                chapter,
                rating,
                difficulty: difficulty || null,
                usefulness: usefulness || null,
                comment: comment.trim() || null
            }, { withCredentials: true });
            setSubmitted(true);
            if (onComplete) onComplete();
        } catch { toast.error('Error al enviar feedback'); }
        finally { setLoading(false); }
    };

    if (submitted) {
        return (
            <Card className="bg-[#5FAF9D]/5 border border-[#5FAF9D]/20 rounded-2xl" data-testid="feedback-submitted">
                <CardContent className="py-4 text-center">
                    <Check className="h-6 w-6 text-[#5FAF9D] mx-auto mb-2" />
                    <p className="text-sm text-[#5FAF9D] font-medium">Gracias por tu feedback</p>
                </CardContent>
            </Card>
        );
    }

    const StarRating = ({ value, onChange, onHover, label }) => (
        <div>
            <Label className="text-xs text-[#7A8A93] mb-1 block">{label}</Label>
            <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map(starNum => (
                    <button key={`star-${starNum}`} type="button" onClick={() => onChange(starNum)}
                        onMouseEnter={() => onHover?.(starNum)} onMouseLeave={() => onHover?.(0)}
                        className="p-0.5 transition-transform hover:scale-110">
                        <Star className={`h-5 w-5 ${starNum <= (onHover ? (hoverRating || value) : value) ? 'fill-[#E8B27A] text-[#E8B27A]' : 'text-[#DCE3E8]'}`} />
                    </button>
                ))}
            </div>
        </div>
    );

    return (
        <Card className="bg-white border border-[#DCE3E8] rounded-2xl" data-testid="module-feedback-form">
            <CardContent className="py-4 space-y-4">
                <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-[#3E5F6E]" />
                    <p className="text-sm font-semibold text-[#1F2A30]">Califique esta actividad</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <StarRating value={rating} onChange={setRating} onHover={setHoverRating} label="Calificación general *" />
                    <StarRating value={difficulty} onChange={setDifficulty} label="Dificultad (opcional)" />
                    <StarRating value={usefulness} onChange={setUsefulness} label="Utilidad (opcional)" />
                </div>

                <div>
                    <Label className="text-xs text-[#7A8A93]">Comentario (opcional)</Label>
                    <Textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="¿Cómo fue tu experiencia con esta actividad?" className="mt-1 border-[#DCE3E8] rounded-xl min-h-[60px] text-sm" data-testid="feedback-comment" />
                </div>

                <Button onClick={handleSubmit} disabled={rating === 0 || loading} className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white rounded-xl" data-testid="submit-feedback-btn">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Enviar feedback
                </Button>
            </CardContent>
        </Card>
    );
}
